import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { Constants } from '@shared/utils/constants';
import { Text, Icon, Label, Stack, ChoiceGroup } from '@fluentui/react';
import Tooltip from '@shared/components/tooltip';
import { TermsStrings } from '@shared/Models';
import { getBillingTermTranslated } from '@shared/utils/pricing';
const pbTelemetry = Constants.Telemetry.PurchaseBlendsTelemetry;
export const BillingTermChoiceGroup = ({ setSelectedTerm, setBillingTerm, items }, context)=>{
    const { id: planId, product, hasFreeTrial, termDuration } = items[0];
    const handleBillingTermChanges = ({ setSelectedTerm, setBillingTerm, planId, product, hasFreeTrial, option })=>{
        const firstTerm = product.getTermByPlanAndBillingTermDuration({
            planId,
            billingTermDuration: option.key
        });
        setBillingTerm({
            id: planId,
            termDuration: option.key
        });
        setSelectedTerm({
            id: planId,
            termId: hasFreeTrial ? firstTerm.freeTrialTermId : firstTerm.termId
        });
    };
    const generateBillingOption = (term, context)=>{
        return {
            key: term,
            text: `${getBillingTermTranslated({
                context,
                term,
                isSubscription: true
            })}`
        };
    };
    const getTermMapping = (context)=>{
        return {
            [TermsStrings.MonthString.toLowerCase()]: generateBillingOption(TermsStrings.Term1MString, context),
            [TermsStrings.Term1MString.toLowerCase()]: generateBillingOption(TermsStrings.Term1MString, context),
            [TermsStrings.YearString.toLowerCase()]: generateBillingOption(TermsStrings.Term1YString, context),
            [TermsStrings.Term1YString.toLowerCase()]: generateBillingOption(TermsStrings.Term1YString, context),
            [TermsStrings.Term2YString.toLowerCase()]: generateBillingOption(TermsStrings.Term2YString, context),
            [TermsStrings.Term3YString.toLowerCase()]: generateBillingOption(TermsStrings.Term3YString, context),
            [TermsStrings.Term4YString.toLowerCase()]: generateBillingOption(TermsStrings.Term4YString, context),
            [TermsStrings.Term5YString.toLowerCase()]: generateBillingOption(TermsStrings.Term5YString, context)
        };
    };
    const getBillingOptions = (items, context)=>{
        // Since sort happens in place, cloning list instead of messing up redux.
        const listOfTerms = [
            ...items[0].product.getTermDurationsByPlanId(items[0].id)
        ];
        return listOfTerms.sort().map((term)=>{
            const billingOptionsMap = getTermMapping(context);
            return billingOptionsMap[term.toLowerCase()];
        });
    };
    const options = useMemo(()=>getBillingOptions(items, context), [
        items,
        context
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Label, {
                children: /*#__PURE__*/ _jsx(Stack, {
                    horizontal: true,
                    verticalAlign: "center",
                    wrap: false,
                    children: /*#__PURE__*/ _jsxs(Text, {
                        className: "ms-fontWeight-semibold",
                        children: [
                            `${context.loc('Checkout_PriceAndBilling_BillingTerm', 'Billing term')} `,
                            /*#__PURE__*/ _jsx(Tooltip, {
                                tooltipHostProps: {
                                    content: context.loc('Checkout_PriceAndBilling_Tooltip_BillingTerm', "This is the plan's subscription period."),
                                    calloutProps: {
                                        gapSpace: 0
                                    }
                                },
                                children: /*#__PURE__*/ _jsx(Icon, {
                                    iconName: "Info",
                                    ariaLabel: context.loc('Checkout_PriceAndBilling_Tooltip_BillingTerm', "This is the plan's subscription period.")
                                })
                            }),
                            /*#__PURE__*/ _jsx(Text, {
                                className: "requiredColor",
                                children: ` *`
                            })
                        ]
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Stack, {
                horizontal: true,
                tokens: {
                    childrenGap: 10
                },
                children: /*#__PURE__*/ _jsx(Stack.Item, {
                    children: (options === null || options === void 0 ? void 0 : options.length) > 1 ? /*#__PURE__*/ _jsx(ChoiceGroup, {
                        "data-bi-id": pbTelemetry.PickBillingTerm,
                        "data-bi-name": pbTelemetry.BillingTermComponent,
                        options: options,
                        onChange: (_, option)=>handleBillingTermChanges({
                                product,
                                planId,
                                setBillingTerm,
                                setSelectedTerm,
                                hasFreeTrial,
                                option
                            }),
                        defaultSelectedKey: termDuration || ''
                    }) : /*#__PURE__*/ _jsx(Text, {
                        children: options === null || options === void 0 ? void 0 : options[0].text
                    })
                })
            })
        ]
    });
};
BillingTermChoiceGroup.contextTypes = {
    locParams: PropTypes.func,
    loc: PropTypes.func
};
